import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Menu, ShoppingCart, User, LogOut, LayoutDashboard, X, DollarSign } from 'lucide-react';

const NAV_LINKS = [
  { href: '/', label: 'Inicio' },
  { href: '/nosotros', label: 'Nosotros' },
  { href: '/servicios', label: 'Servicios' },
  { href: 'https://soporte.kindtechnologies.net/portal/es/home', label: 'Soporte', external: true },
  { href: '/clientes', label: 'Clientes' },
  { href: '/contacto', label: 'Contacto' },
];

export function SiteHeader() {
  const { user, logout, isAdmin } = useAuth();
  const { cartCount } = useCart();
  const { currency, toggleCurrency } = useCurrency();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => setScrolled(window.scrollY > 10), { passive: true });
  }

  const isActive = (path) => location.pathname === path;

  return (
    <header className={`sticky top-0 z-50 w-full bg-[#010101] text-white border-b border-white/10 backdrop-blur-sm transition-shadow duration-150 ${scrolled ? 'shadow-sm' : ''}`} data-testid="site-header">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <img src="/logo-kind.png" alt="KIND TECHNOLOGIES" className="h-10 w-auto" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map(link => link.external ? (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`nav-${link.label.toLowerCase()}`}
                className="px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 text-gray-400 hover:text-white hover:bg-white/10"
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                  isActive(link.href)
                    ? 'text-white bg-white/10'
                    : 'text-gray-400 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right section */}
          <div className="flex items-center gap-2">
            {/* Currency Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleCurrency}
              className="hidden sm:inline-flex gap-1 text-gray-300 hover:text-white hover:bg-white/10"
              data-testid="currency-toggle"
              title={`Cambiar a ${currency === 'MXN' ? 'USD' : 'MXN'}`}
            >
              <DollarSign className="h-4 w-4" />
              {currency}
            </Button>
            
            <Link to="/tienda" data-testid="header-store-cta-button">
              <Button size="sm" className="hidden sm:inline-flex bg-white text-[#010101] hover:bg-gray-200 active:scale-[0.98]">
                Tienda en Línea
              </Button>
            </Link>

            {user && (
              <Link to="/carrito" className="relative p-2 hover:bg-white/10 rounded-md transition-colors duration-150" data-testid="header-cart-button">
                <ShoppingCart className="h-5 w-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#295F80] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                    {cartCount}
                  </span>
                )}
              </Link>
            )}

            {user ? (
              <div className="hidden sm:flex items-center gap-1">
                {isAdmin && (
                  <Link to="/admin" data-testid="header-admin-link">
                    <Button variant="ghost" size="sm" className="gap-1 text-gray-300 hover:text-white hover:bg-white/10">
                      <LayoutDashboard className="h-4 w-4" /> Admin
                    </Button>
                  </Link>
                )}
                <Link to="/perfil" data-testid="header-profile-link">
                  <Button variant="ghost" size="sm" className="gap-1 text-gray-300 hover:text-white hover:bg-white/10">
                    <User className="h-4 w-4" /> {user.name?.split(' ')[0]}
                  </Button>
                </Link>
                <Button variant="ghost" size="sm" onClick={logout} data-testid="header-logout-button" className="text-gray-300 hover:text-white hover:bg-white/10">
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Link to="/login" className="hidden sm:inline-flex">
                <Button variant="outline" size="sm" data-testid="header-login-button" className="border-white/30 text-white hover:bg-white/10">Iniciar Sesión</Button>
              </Link>
            )}

            {/* Mobile menu */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="sm" data-testid="header-mobile-menu" className="text-white hover:bg-white/10">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-0">
                <div className="flex flex-col h-full">
                  <div className="p-4 border-b flex items-center justify-between">
                    <span className="font-display font-bold">
                      <img src="/logo-kind.png" alt="KIND TECHNOLOGIES" className="h-8 w-auto" />
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => setOpen(false)}><X className="h-4 w-4" /></Button>
                  </div>
                  <nav className="flex-1 p-4 space-y-1">
                    {NAV_LINKS.map(link => link.external ? (
                      <a
                        key={link.href}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setOpen(false)}
                        className="block px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 text-muted-foreground hover:bg-secondary/50"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setOpen(false)}
                        className={`block px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                          isActive(link.href) ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:bg-secondary/50'
                        }`}
                      >
                        {link.label}
                      </Link>
                    ))}
                    <Link to="/tienda" onClick={() => setOpen(false)} className="block px-3 py-2 rounded-md text-sm font-semibold text-foreground bg-secondary">
                      Tienda en Línea
                    </Link>
                  </nav>
                  <div className="p-4 border-t space-y-2">
                    {user ? (
                      <>
                        <Link to="/perfil" onClick={() => setOpen(false)}>
                          <Button variant="outline" className="w-full justify-start gap-2"><User className="h-4 w-4" /> Mi Perfil</Button>
                        </Link>
                        {isAdmin && (
                          <Link to="/admin" onClick={() => setOpen(false)}>
                            <Button variant="outline" className="w-full justify-start gap-2"><LayoutDashboard className="h-4 w-4" /> Admin</Button>
                          </Link>
                        )}
                        <Button variant="ghost" className="w-full justify-start gap-2" onClick={() => { logout(); setOpen(false); navigate('/'); }}>
                          <LogOut className="h-4 w-4" /> Cerrar Sesión
                        </Button>
                      </>
                    ) : (
                      <Link to="/login" onClick={() => setOpen(false)}>
                        <Button className="w-full">Iniciar Sesión</Button>
                      </Link>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
